import {
  Constraints,
  ElementOperator,
  Event,
  Platform,
  SegmentDefinition,
  TimeRange as SegmentTimeRange,
} from '@fullstory/proto/fs/api/segment';
import { cloneDeep } from 'lodash';
import { assertExhaustive } from '@fullstory/typeutils';
import { Duration, int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { toProtoDuration } from '@fullstory/search-converter/helpers';
import * as sharedSchema from '@fullstory/client-api/goimports/fs_shared_schema';
import * as alexa from '@fullstory/proto/fs/services/alexa';
import { ToPlatformMap } from '@fullstory/client-api/goimports/fs_api_segment_convert';
import { DurationUnit } from '@fullstory/search-types';
import { UnnamedSegment } from '@fullstory/proto/fs/services/dx-objects';
import { parseUnscopedFlatVars } from '@fullstory/util/vars';
import * as vars from '@fullstory/proto/fs/api/vars';

type ConstraintsMaker =
  | (Partial<Omit<Constraints, 'timeRange'>> & Pick<Constraints, 'timeRange'>)
  | null;
type SegmentDefinitionMaker = Partial<Omit<SegmentDefinition, 'constraints'>> &
  Record<'constraints', ConstraintsMaker>;

// makeSegmentDefinition is a helper that takes a partial segment definition and produces a fully hydrated segment definition.
// Only the properties that are optional are made partial.
// Of note: A constraints property must be provided since a time range must be provided.
export function makeSegmentDefinition(
  segmentDefinition: SegmentDefinitionMaker,
): SegmentDefinition {
  return {
    constraints: {
      inOrder: segmentDefinition.constraints?.inOrder || false,
      inSameSession: segmentDefinition.constraints?.inSameSession || false,
      platform: segmentDefinition.constraints?.platform || Platform.PLATFORM_INVALID,
      timeRange: segmentDefinition.constraints?.timeRange ?? null,
      completeSessions: segmentDefinition.constraints?.completeSessions || false,
      withinTime: segmentDefinition.constraints?.withinTime || Duration.zero,
      eventThresholds: segmentDefinition.constraints?.eventThresholds ?? [],
    },
    excludeConstraints: segmentDefinition.excludeConstraints ?? null,
    userProperties: segmentDefinition.userProperties || [],
    excludeUserProperties: segmentDefinition.excludeUserProperties || [],
    behaviors: segmentDefinition.behaviors || [],
    excludeBehaviors: segmentDefinition.excludeBehaviors || [],
  };
}

export function noneDisabled(): UnnamedSegment.Disabled {
  return {
    userProperties: [],
    excludeUserProperties: [],
    behaviors: [],
    excludeBehaviors: [],
  };
}

export const disabledUnnamedSegment: UnnamedSegment.Disabled = {
  userProperties: [],
  excludeUserProperties: [],
  behaviors: [],
  excludeBehaviors: [],
};

export function addEventToSegmentDefinition(
  segmentDefinition: SegmentDefinition,
  event: Event,
): SegmentDefinition {
  const newSegmentDefinition = cloneDeep(
    makeSegmentDefinition(
      segmentDefinition || {
        // just default to something sane instead of all time when there is no segment
        constraints: {
          timeRange: {
            startTime: 'NOW/DAY-29DAY',
            endTime: 'NOW/DAY+1DAY',
          },
        },
      },
    ),
  );
  newSegmentDefinition.behaviors.push({ event });
  return newSegmentDefinition;
}

export enum PerfTypes {
  DomContent = 'LoadDomContentTime',
  FirstPaint = 'LoadFirstPaintTime',
  LargestPaint = 'LoadLargestPaintTime',
  LoadTime = 'LoadEventTime',
  InteractionToNextPaint = 'InteractionToNextPaint',
  TimeToInteractive = 'TimeToInteractive',
  TotalBlockingTime = 'TotalBlockingTime',
  TimeToFirstByte = 'TimeToFirstByte',
}

export function makeEventFromPerf(type: PerfTypes, durationMs: number): Event {
  const duration = Duration.fromMillis(durationMs);
  switch (type) {
    case PerfTypes.DomContent:
      return { primary: { domContentLoaded: { greaterThan: { value: duration } } }, deps: [] };
    case PerfTypes.FirstPaint:
      return { primary: { firstMeaningfulPaint: { greaterThan: { value: duration } } }, deps: [] };
    case PerfTypes.LargestPaint:
      return {
        primary: { largestContentfulPaint: { greaterThan: { value: duration } } },
        deps: [],
      };
    case PerfTypes.LoadTime:
      return { primary: { pageLoad: { greaterThan: { value: duration } } }, deps: [] };
    case PerfTypes.InteractionToNextPaint:
      return {
        primary: { interactionToNextPaint: { greaterThan: { value: duration } } },
        deps: [],
      };
    case PerfTypes.TimeToInteractive:
      return { primary: { timeToInteractive: { greaterThan: { value: duration } } }, deps: [] };
    case PerfTypes.TotalBlockingTime:
      return { primary: { totalBlockingTime: { greaterThan: { value: duration } } }, deps: [] };
    case PerfTypes.TimeToFirstByte:
      return { primary: { timeToFirstByte: { greaterThan: { value: duration } } }, deps: [] };
    default:
      assertExhaustive(type);
  }
}

export function makeEventFromElementOperator(
  operator: ElementOperator,
  type?: alexa.ComputeElementStatsRequest.StatsType,
): Event {
  const event: Event = { primary: null, deps: [] };
  if (type) {
    switch (type) {
      case alexa.ComputeElementStatsRequest.StatsType.STATS_TYPE_DEAD_CLICKS:
        event.primary = { deadClick: operator };
        break;
      case alexa.ComputeElementStatsRequest.StatsType.STATS_TYPE_RAGE_CLICKS:
        event.primary = { rageClick: operator };
        break;
      case alexa.ComputeElementStatsRequest.StatsType.STATS_TYPE_ERROR_CLICKS:
        event.primary = { errorClick: operator };
        break;
      default:
        console.log(`Unknown click type: ${type}`);
        break;
    }
  }
  if (!event.primary) {
    // either a type was not provided or we are falling back to any click due to unknown click type
    event.primary = { click: operator };
  }
  return event;
}

export function makeEventFromIP(ip: string): Event {
  return {
    primary: null,
    deps: [{ ipAddress: { equals: { value: [ip] } } }],
  };
}

export function makeEventFromCountry(country: string): Event {
  return {
    primary: null,
    deps: [{ country: { equals: { value: [country] } } }],
  };
}

export function makeSegmentDefinitionFromEvent(
  event: Event,
  timeRange: SegmentTimeRange,
): SegmentDefinition {
  return makeSegmentDefinition({
    constraints: {
      timeRange,
    },
    behaviors: [{ event }],
  });
}

export function clipListSharedToProto(clips: sharedSchema.Clip[]): alexa.Clip[] {
  return clips.map(c => clipSharedToProto(c));
}

export function clipSharedToProto(c: sharedSchema.Clip): alexa.Clip {
  const eventMarkers = markerListSharedToProto(c.Markers);
  const lastEventMillis = eventMarkers
    .map(marker => Timestamp.toMillis(marker.pageStartAt) + marker.pageOffset)
    .reduce((left, right) => (left > right ? left : right), 0);
  const lastEventAt = Timestamp.fromMillis(lastEventMillis);
  return {
    eventMarkers,
    lastMatchingCity: c.LastMatchingCity,
    lastMatchingPage: c.LastMatchingPage,
    lastMatchingBrowser: c.LastMatchingBrowser,
    lastMatchingDevice: c.LastMatchingDevice,
    lastMatchingOperatingSystem: c.LastMatchingOperatingSystem,
    lastMatchingPlatform: ToPlatformMap.get(c.LastMatchingPlatform) ?? Platform.PLATFORM_INVALID,
    lastMatchingOperatingSystemVersion: c.LastMatchingOperatingSystemVersion,
    lastMatchingAppVersion: c.LastMatchingAppVersion,
    lastMatchingDeviceModel: c.LastMatchingDeviceModel,
    lastMatchingAppName: c.LastMatchingAppName,
    lastMatchingEventName: c.LastMatchingEventName,
    lastMatchingEventVars: parseUnscopedFlatVars(
      vars.Scope.evt,
      lastEventAt,
      c.LastMatchingEventVars,
    ),
    lastMatchingCaptureSourceIntegration: c.LastMatchingCaptureSourceIntegration,
  };
}

export function markerListSharedToProto(
  markers: (sharedSchema.EventMarker | null)[] | null,
): alexa.EventMarker[] {
  return markers ? markers.map(m => markerSharedToProto(m)) : [];
}

export function markerSharedToProto(m: sharedSchema.EventMarker | null): alexa.EventMarker {
  return {
    indvId: m ? (m.IndvId as int64) : int64.nil,
    userId: m ? (m.UserId as int64) : int64.nil,
    sessionId: m ? (m.SessionId as int64) : int64.nil,
    pageId: m ? (m.PageId as int64) : int64.nil,
    pageStartAt: m ? Timestamp.fromMillis(m.PageStart) : null,
    pageOffset: m ? m.EventPageOffsetMs : 0,
    sessionOffset: m ? m.EventSessionOffsetMs : 0,
  };
}

export function sessionListSharedToProto(sessions: sharedSchema.SearchSession[]): alexa.Session[] {
  return sessions.map(s => sessionSharedToProto(s));
}

export function sessionSharedToProto(s: sharedSchema.SearchSession): alexa.Session {
  const exclamIndex = s.UserSessionId.indexOf('!');
  const lmp = ToPlatformMap.get(s.LastMatchingPlatform) ?? Platform.PLATFORM_INVALID;
  const lastEventAt = Timestamp.fromMillis(s.LastEventTime);
  return {
    indvId: s.IndvId as int64,
    userId: s.UserSessionId.substring(0, exclamIndex) as int64,
    sessionId: s.UserSessionId.substring(exclamIndex + 1) as int64,
    lastEventAt,
    duration: toProtoDuration({ unit: DurationUnit.second, scalar: s.DurationInSeconds }),
    numPages: s.NumPages,
    numEvents: s.NumEvents,
    lastMatchingCity: s.LastMatchingCity,
    lastMatchingPage: s.LastMatchingPage,
    lastMatchingBrowser: s.LastMatchingBrowser,
    lastMatchingDevice: s.LastMatchingDevice,
    lastMatchingOperatingSystem: s.LastMatchingOperatingSystem,
    lastMatchingPlatform: lmp,
    lastMatchingOperatingSystemVersion: s.LastMatchingOperatingSystemVersion,
    lastMatchingAppVersion: s.LastMatchingAppVersion,
    lastMatchingDeviceModel: s.LastMatchingDeviceModel,
    lastMatchingAppName: s.LastMatchingAppName,
    lastMatchingEventName: s.LastMatchingEventName,
    lastMatchingEventVars: parseUnscopedFlatVars(
      vars.Scope.evt,
      lastEventAt,
      s.LastMatchingEventVars,
    ),
    lastMatchingCaptureSourceIntegration: s.LastMatchingCaptureSourceIntegration,
  };
}

export const searchieTypeToReadable: Map<alexa.SearchieType, string> = new Map([
  [alexa.SearchieType.SEARCHIE_TYPE_TOP_ERROR_CLICKS, 'SearchieTopErrorClicks'],
  [alexa.SearchieType.SEARCHIE_TYPE_TOP_RAGE_CLICKS, 'SearchieTopRageClicks'],
  [alexa.SearchieType.SEARCHIE_TYPE_TOP_DEAD_CLICKS, 'SearchieTopDeadClicks'],
  [alexa.SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_REFERER_HOST, 'SearchieTrafficByRefererHost'],
  [alexa.SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_SOURCE, 'SearchieTrafficByUtmSource'],
  [alexa.SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_CAMPAIGN, 'SearchieTrafficByUtmCampaign'],
  [alexa.SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_MEDIUM, 'SearchieTrafficByUtmMedium'],
  [alexa.SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_BROWSER, 'SearchieTrafficByBrowser'],
  [alexa.SearchieType.SEARCHIE_TYPE_DEVICE_BREAKDOWN, 'SearchieDeviceBreakdown'],
  [alexa.SearchieType.SEARCHIE_TYPE_EVENT_FUNNEL, 'SearchieEventFunnel'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOST_ACTIVE_USERS, 'SearchieMostActiveUsers'],
  [alexa.SearchieType.SEARCHIE_TYPE_SEGMENT_HEALTH, 'SearchieSegmentHealth'],
  [alexa.SearchieType.SEARCHIE_TYPE_TOP_PAGES, 'SearchieTopPages'],
  [alexa.SearchieType.SEARCHIE_TYPE_RAGE_GRADE, 'SearchieRageGrade'],
  [alexa.SearchieType.SEARCHIE_TYPE_ACTIVITY_SUMMARY, 'SearchieActivitySummary'],
  [alexa.SearchieType.SEARCHIE_TYPE_DAILY_ACTIVES, 'SearchieDailyActives'],
  [alexa.SearchieType.SEARCHIE_TYPE_MATCHING_USERS, 'SearchieMatchingUsers'],
  [alexa.SearchieType.SEARCHIE_TYPE_TIME_RANGE_COUNTS, 'SearchieTimeRangeCounts'],
  [alexa.SearchieType.SEARCHIE_TYPE_FEEDBACK, 'SearchieFeedback'],
  [alexa.SearchieType.SEARCHIE_TYPE_SCREEN_RESOLUTIONS, 'SearchieScreenResolutions'],
  [alexa.SearchieType.SEARCHIE_TYPE_SLOW_PAGES, 'SearchieSlowPages'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOBILE_DEVICES, 'SearchieMobileDevices'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOBILE_APP_VERSIONS, 'SearchieMobileAppVersions'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOBILE_RESOLUTIONS, 'SearchieMobileResolutions'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOBILE_OS_VERSIONS, 'SearchieMobileOsVersions'],
  [alexa.SearchieType.SEARCHIE_TYPE_API_ERRORS, 'SearchieApiErrors'],
  [alexa.SearchieType.SEARCHIE_TYPE_MOST_COMMON_ENTRY_PAGES, 'SearchieMostCommonEntryPages'],
  [alexa.SearchieType.SEARCHIE_TYPE_USER_TRENDS_CARD, 'SearchieUserTrendsCard'],
]);
